// Core fetch
export {
    ApiError,
    AuthSessionError,
    apiFetch,
    apiUpload,
    CLIENT_PLATFORM_HEADER,
    getApiBaseUrl,
    getClientPlatformHeader,
    parseApiErrorResponse,
    publicApiFetch,
    setApiBaseUrl,
    setClientPlatformHeader,
    setNativeClientHeader,
} from './fetch';
export type {
    ApiFetchInit,
    ApiUploadInit,
    ClientPlatform,
    ChangedSinceResult,
    PublicApiFetchInit,
} from './fetch';
export { apiFetchWithTimeout } from './fetch-timeout';
export {
    ApiRequestTimeoutError,
    DEFAULT_API_TIMEOUT_MS,
    createRequestSignal,
} from './request-timeout';
export { adminFetchJson, adminFetchOk } from './admin-fetch';
export { createCorrelationId, getCorrelationIdFromResponse, withCorrelationHeaders } from './correlation';
export {
  emitApiRequestPerformance,
  setApiPerformanceObserver,
  toApiRouteFamily,
} from './performance-telemetry';
export { createApiPerformanceReporter } from './performance-reporter';
export type {
  ApiPerformanceReporter,
  ApiPerformanceReporterOptions,
} from './performance-reporter';
export { createClientMetricReporter, toPageRouteFamily } from './client-metric-reporter';
export type {
  ClientMetricEvent,
  ClientMetricName,
  ClientMetricReporter,
  ClientMetricReporterOptions,
} from './client-metric-reporter';
export type {
  ApiPerformanceObserver,
  ApiRequestOutcome,
  ApiRequestPerformanceEvent,
} from './performance-telemetry';

// Credits / billing
export {
    fetchCreditBalance,
    fetchCreditHistory,
    fetchUserCreditPackages,
    fetchUserPaymentMethods,
} from './credits';
export type {
    CreditBalanceResponse,
    CreditHistoryFilters,
    CreditHistoryResponse,
    CreditTransactionResponse,
    PaymentMethodDto,
    UserCreditPackageDto,
} from './credits';
export {
    fetchPlanUsage,
    startTrialCheckout,
} from './billing';
export type {
    PlanUsageDto,
    TrialCheckoutResponse,
} from './billing';
export {
    fetchDocuments,
    fetchDocumentsPage,
    fetchDocumentsChangedSince,
    fetchDocument,
    fetchDocumentStats,
    fetchSentDeliveries,
    archiveEnviosDelivery,
    unarchiveEnviosDelivery,
    cancelSentDelivery,
} from './documents';
export type {
    DashboardStatsDto,
    DocumentListItem,
    PagedDocumentsResult,
} from './documents';
export {
    fetchDashboardInsights,
    fetchDashboardSummary,
} from './dashboard';
export type { DashboardSummary } from './dashboard';
export {
    fetchAdminIncidents,
    fetchAdminIncidentDetail,
    patchAdminIncidentTriage,
    fetchAdminApplicationErrors,
    fetchAdminDocumentActivity,
    fetchAdminOperationsCenter,
    fetchAdminRunbook,
    fetchAdminGlobalSearch,
    fetchAdminUserDetail,
    fetchAdminUserCredits,
    addAdminUserCredits,
    fetchAdminAuditLogs,
    fetchAdminDunning,
    fetchAdminInvoiceSummary,
    fetchAdminInvoices,
    fetchAdminInvoice,
    fetchAdminMissingInvoicePayments,
    createAdminInvoiceFromPayment,
    attachAdminInvoiceManual,
    cancelAdminInvoice,
    fetchAdminContactSubmissions,
    markAdminContactSubmissionRead,
    deleteAdminContactSubmission,
    fetchAdminFeatureFlags,
    toggleAdminFeatureFlag,
    fetchAdminBreakGlass,
    fetchAdminTicketDetail,
    revokeAdminSession,
    revokeAllUserSessions,
    revokeUserTokens,
    suspendAdminUser,
    restoreAdminUser,
    forcePasswordResetAdminUser,
    approveAdminUser,
    rejectAdminUser,
    setAdminUserTag,
    fetchAdminApiGeo,
    fetchAdminDownloadGeo,
    fetchAdminLandingSections,
    fetchAdminLandingSources,
    fetchAdminLandingSummary,
    fetchAdminLandingDevices,
    fetchAdminLandingPages,
    fetchAdminLandingHeatmap,
    fetchAdminLandingGeo,
    patchAdminTicket,
    postAdminTicketReply,
    fetchAdminActiveUsers,
    fetchAdminDashboard,
    fetchAdminDashboardCharts,
    fetchAdminDashboardPage,
    fetchAdminDocumentActivitySummary,
    fetchAdminDocuments,
    fetchAdminPresenceHistory,
    fetchAdminPresenceOnline,
    fetchAdminRevenue,
    fetchAdminSessions,
    fetchAdminSessionsPaged,
    fetchAdminTickets,
    fetchAdminUsers,
    fetchAdminSafetyReports,
    fetchAdminSafetyReportsSummary,
    fetchAdminSafetyReport,
    triageAdminSafetyReport,
    fetchAdminCompanyThreads,
    fetchAdminCompanyMessages,
    sendAdminCompanyMessage,
    sendAdminNotification,
} from './admin';
export type {
    AdminActiveUsersDto,
    AdminDashboardChartsDto,
    AdminDashboardDto,
    AdminDashboardPageData,
    AdminDocumentActivitySummaryDto,
    AdminDocumentInventoryStatus,
    AdminDocumentListFilters,
    AdminDocumentListItem,
    AdminDocumentStatusFilter,
    AdminPagedDocumentsResult,
    AdminPagedTicketsResult,
    AdminPagedUsersResult,
    AdminPresenceHistoryPoint,
    AdminPresenceOnline,
    AdminPresenceUser,
    AdminRevenueDto,
    AdminSessionItem,
    AdminSessionQuery,
    AdminTicketListItem,
    AdminUserListItem,
    AdminIncidentsDashboard,
    AdminIncidentDetail,
    AdminIncidentTriageRequest,
    AdminApplicationErrorLog,
    AdminDocumentActivityEvent,
    AdminDocumentActivityFeed,
    AdminOperationsCenter,
    AdminAttentionQueue,
    AdminRunbook,
    AdminRunbookEntry,
    AdminGlobalSearchResult,
    AdminSearchHit,
    AdminUserDetail,
    AdminCreditBalance,
    AdminAuditLogItem,
    AdminDunningDashboard,
    AdminDunningUser,
    AdminInvoiceDto,
    AdminInvoiceSummaryDto,
    AdminMissingInvoicePaymentDto,
    AdminContactSubmission,
    AdminFeatureFlag,
    BreakGlassAccess,
    SupportTicketDetail,
    SupportTicketMessage,
    AdminPagedResult,
    AdminCompanyMessageThread,
    AdminCompanyMessageItem,
} from './admin';
export {
    fetchStorageDocuments,
    fetchStorageSummary,
} from './storage';
export type {
    StorageDocumentDto,
    StorageSummaryDto,
} from './storage';

// Public API
export {
    fetchCreditPackages,
    fetchCreditSubscriptionTiers,
    fetchMobileReleaseInfo,
    fetchPlans,
    fetchPlatformStats,
    fetchPremiumAddOnsCatalog,
    fetchProductKit,
    fetchSiteConfig,
} from './public-api';

// Notifications
export {
    deleteAllNotifications,
    deleteNotification,
    fetchNotification,
    fetchNotifications,
    fetchNotificationsPage,
    fetchUnreadCount,
    getNotificationActionError,
    markAllAsRead,
    markAsOpened,
    markAsRead,
    normalizeNotificationItem,
} from './notifications';
export type { NotificationsListResponse } from './notifications';

// Push
export {
    getCurrentSubscription, getPushPermission, getVapidPublicKey,
    isPushSupported, subscribeToPush,
    unsubscribeFromPush
} from './push';

// Document shares
export {
    acceptDocumentShare,
    createDocumentShare,
    fetchDocumentShare,
    fetchDocumentShareView,
    fetchPendingShareCount,
    fetchShareInbox,
    fetchShareSent,
    parseContentDispositionFilename,
    rejectDocumentShare,
    reportDocumentShareSecurityEvent,
    revokeDocumentShare,
    archiveSentShare,
    unarchiveSentShare,
    SHARE_ACCEPTANCE_EXPIRED,
    validateRecipientEmail,
    type DocumentShareSecurityEventType,
} from './document-shares';
export {
    createClientMessageId,
    createConversation,
    fetchConversationMedia,
    fetchConversationMessages,
    fetchConversationThreadSummary,
    fetchConversationSendQuote,
    fetchConversations,
    fetchConversationsChangedSince,
    fetchPeerPreKeyBundle,
    archiveConversation,
    unarchiveConversation,
    muteConversation,
    unmuteConversation,
    setConversationMessageReaction,
    removeConversationMessageReaction,
    editConversationMessage,
    deleteConversationMessage,
    fetchPinnedMessages,
    pinConversationMessage,
    unpinConversationMessage,
    searchConversationMessages,
    markConversationRead,
    notifyConversationTyping,
    registerConversationCryptoDevice,
    sendConversationDocument,
    sendConversationText,
} from './conversations';
export {
    addRecipientsBatch,
} from './recipients';
export type { AddRecipientsBatchResponse, BatchRecipientInput } from './recipients';
export {
    enqueueDocumentUploadAsync,
} from './document-upload';
export type { EnqueueUploadJobResponse } from './document-upload';
export {
    enqueueGeneration,
    regenerateAllCopies,
    resendRecipientDownloadEmail,
    fetchActiveGenerationJob,
    fetchActiveGenerationJobs,
    fetchGenerationJob,
    waitForGenerationJob,
} from './generation-jobs';
export {
    confirmPublicErasure,
    createDocumentDeletionRequest,
    declinePublicErasure,
    fetchDocumentDeletionRequests,
    fetchPublicErasureInfo,
    ownerDeleteDocument,
} from './document-deletion';
export type {
    DocumentDeletionAckDto,
    DocumentDeletionRequestDto,
    PublicErasureInfoDto,
} from './document-deletion';
export type {
    ActiveGenerationJobDto,
    EnqueueGenerationJobResponse,
    GenerationJobDto,
    GenerationJobPhase,
    GenerationJobStatus,
    RecipientLinkDto,
} from './generation-jobs';
export { checkHandleAvailability, fetchConversationReadiness, searchUsers, updateDiscoverability, updatePublicHandle } from './user-search';
export { fetchProtectionPresetsSelection } from './protection-presets';
export {
    fetchUserBlocks,
    blockUser,
    unblockUser,
    createSafetyReport,
} from './user-safety';
export { fetchUserProfilePreview } from './user-profile';
export { fetchPushDevices, fetchPushPreferences, sendPushSelfTest, updatePushPreferences } from './push-devices';
export type { PushPreferences, PushSelfTestResult, UserPushDeviceDto, UserPushDevicesResponse } from './push-devices';
export { REQUIRES_NATIVE_APP_CODE } from '@tracedocuments/types';

// Error handling
export { getApiErrorMessage, humanizeApiError } from './errors';

// Re-export all types for convenience
export type {
    CreditPackageDto,
    CreditSubscriptionTierDto,
    MobileReleaseInfoDto,
    NotificationItem,
    PlanDto,
    PlanFeatureDto,
    PlatformStatsDto,
    PremiumAddOnCatalogItemDto,
    ProductKitDto,
    RejectDocumentShareRequest,
    SiteConfigDto,
} from '@tracedocuments/types';

